/**
 * LogoIcon.js — Brand logo rendering for the Settings integration grid.
 *
 * Pulls vector paths from the `simple-icons` package where available. A few
 * tiles need inline fallbacks because the package omits them (Freshdesk,
 * OpenAI) or names them ambiguously (AWS / S3 / R2).
 */
import React from 'react';
import {
  siJira, siFigma, siConfluence, siNotion, siLinear, siAsana, siClickup,
  siZendesk, siIntercom, siHubspot, siGoogledrive, siAnthropic, siClaude,
  siGooglecloud, siGooglegemini, siDropbox, siBox,
} from 'simple-icons';

// ── Inline fallbacks ─────────────────────────────────────────────────────────
// Minimal monogram-style marks for brands not in simple-icons. These are
// styled as a coloured square with the wordmark initial — clearly a fallback
// but recognisable in context.

// Brand marks keep their official colours (not app theme tokens).
function Monogram({ letters, bg, fg = '#FFFFFF', size }) {
  const px = `${size}px`;
  return (
    <div
      aria-hidden
      style={{
        width: px, height: px,
        borderRadius: Math.round(size * 0.18),
        background: bg, color: fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit',
        fontSize: Math.round(size * 0.42),
        fontWeight: 700, letterSpacing: '-0.02em',
        lineHeight: 1,
      }}
    >
      {letters}
    </div>
  );
}

// Anthropic's claude icon in simple-icons renders as a single asterisk. We
// pair it with the brand orange to read as "Anthropic" rather than "Claude".
const ANTHROPIC = siClaude || siAnthropic;

// ── Registry ─────────────────────────────────────────────────────────────────

const REGISTRY = {
  jira:              { icon: siJira,        hex: siJira?.hex },
  linear:            { icon: siLinear,      hex: siLinear?.hex },
  asana:             { icon: siAsana,       hex: siAsana?.hex },
  clickup:           { icon: siClickup,     hex: siClickup?.hex },
  confluence:        { icon: siConfluence,  hex: siConfluence?.hex },
  notion:            { icon: siNotion,      hex: siNotion?.hex || '000000' },
  figma:             { icon: siFigma,       hex: siFigma?.hex },
  zendesk:           { icon: siZendesk,     hex: siZendesk?.hex },
  intercom:          { icon: siIntercom,    hex: siIntercom?.hex },
  hubspot:           { icon: siHubspot,     hex: siHubspot?.hex },
  google_drive:      { icon: siGoogledrive, hex: siGoogledrive?.hex },
  // GCS uses the Google Cloud icon (siGooglecloud); falls back to monogram if unavailable
  google_cloud_storage: { icon: siGooglecloud, hex: siGooglecloud?.hex || '4285F4' },
  gcs:               { icon: siGooglecloud, hex: siGooglecloud?.hex || '4285F4' },
  // Gemini icon (simple-icons renamed siGooglebard → siGooglegemini); monogram fallback
  gemini:            { icon: siGooglegemini, hex: siGooglegemini?.hex || '4285F4' },
  anthropic:         { icon: ANTHROPIC,     hex: 'D97706' }, // brand orange overrides simple-icons grey
  dropbox:           { icon: siDropbox,     hex: siDropbox?.hex || '0061FF' },
  box:               { icon: siBox,         hex: siBox?.hex     || '0061D5' },
};

// Brands without a simple-icons entry — rendered as monograms.
const FALLBACK = {
  freshdesk:            { letters: 'F',   bg: '#25C16F' },
  openai:               { letters: 'AI',  bg: '#10A37F' },
  aws_s3:               { letters: 'S3',  bg: '#FF9900' },
  google_cloud_storage: { letters: 'GCS', bg: '#4285F4' },
  gcs:                  { letters: 'GCS', bg: '#4285F4' },
  gemini:               { letters: 'G',   bg: '#4285F4' },
};

// `decorative` hides the mark from assistive tech when the provider name is
// already visible next to it (tiles, detail headers).
export default function LogoIcon({ id, size = 32, decorative = false }) {
  const entry = REGISTRY[id];
  if (entry && entry.icon) {
    return (
      <svg
        role={decorative ? undefined : 'img'}
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-label={decorative ? undefined : entry.icon.title}
        aria-hidden={decorative ? 'true' : undefined}
        focusable="false"
      >
        <path d={entry.icon.path} fill={`#${entry.hex}`} />
      </svg>
    );
  }
  const fb = FALLBACK[id];
  if (fb) return <Monogram {...fb} size={size} />;
  // Last resort — neutral square so layout doesn't collapse.
  return <Monogram letters="?" bg="#6B7280" size={size} />;
}
